/**
 * Aba Precisão: quanto a fila errou de fato. Cada foto guardada disse "a
 * fila de N ambientes escoa em D dias úteis"; os términos que vieram depois
 * dizem em quantos ela escoou. Com 3 medições maduras a faixa de erro usada
 * na página passa a ser a medida; antes disso vale a referência da análise
 * de 07/10/2026.
 *
 * Também é aqui que entram as cópias antigas da planilha (histórico para o
 * ritmo), só para quem configura a planilha.
 */
import React, { useState } from "react";

import AsyncButton from "../../components/AsyncButton";
import { CardItem, CardList, CardMeta, CardSub, CardTitulo, CardTopo } from "../../components/ListaCompacta/styles";
import { useToast } from "../../context/ToastContext";
import { FONTE_MEDIDA, MATURACAO_DU } from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import {
  dataCompleta,
  percentualComSinal,
  textoFaixaDePrecisao,
  textoFonteDaPrecisao,
  umaCasa,
} from "./apresentacao";
import { Aviso, Bloco, Campos, Grade, Regua, Vazio } from "./styles";

const ROTULO_ORIGEM = { drive: "Drive", importacao: "Cópia importada" };
const COLUNAS_MEDICOES = "minmax(0, 1.2fr) repeat(5, minmax(0, 1fr))";
const COLUNAS_FOTOS = "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 0.8fr)";

function linhasDeMedicao(precisao) {
  const medidas = (precisao?.medicoes || []).map((m) => ({ ...m, pendente: false }));
  const pendentes = (precisao?.pendentes || []).map((m) => ({ ...m, pendente: true }));
  return [...medidas, ...pendentes].sort((a, b) => (a.data_referencia < b.data_referencia ? 1 : -1));
}

function Medicoes({ linhas, compacto }) {
  if (linhas.length === 0) {
    return <Vazio>Nenhuma foto guardada tem projeção para comparar ainda.</Vazio>;
  }
  if (compacto) {
    return (
      <CardList>
        {linhas.map((m) => (
          <CardItem key={m.data_referencia}>
            <CardTopo>
              <CardTitulo>Foto de {dataCompleta(m.data_referencia)}</CardTitulo>
              <span className="selo">{m.pendente ? "aguardando" : percentualComSinal(m.erro)}</span>
            </CardTopo>
            <CardSub>
              previsto {umaCasa(m.previsto)} · real {m.pendente ? "—" : m.real} dias úteis
            </CardSub>
            <CardMeta>
              <span className="par">
                <span className="k">fila</span> <b>{m.fila}</b>
              </span>
              <span className="par">
                <span className="sep" aria-hidden="true">· </span>
                <span className="k">ritmo</span> <b>{umaCasa(m.ritmo)}</b>
                <span className="k"> por dia útil</span>
              </span>
            </CardMeta>
          </CardItem>
        ))}
      </CardList>
    );
  }
  return (
    <Grade $colunas={COLUNAS_MEDICOES} role="table" aria-label="Medições da precisão">
      <span className="cab">Foto de</span>
      <span className="cab">Fila</span>
      <span className="cab">Ritmo/dia útil</span>
      <span className="cab">Previsto (du)</span>
      <span className="cab">Real (du)</span>
      <span className="cab">Erro</span>
      {linhas.map((m) => (
        <React.Fragment key={m.data_referencia}>
          <span className="forte">{dataCompleta(m.data_referencia)}</span>
          <span>{m.fila}</span>
          <span>{umaCasa(m.ritmo)}</span>
          <span>{umaCasa(m.previsto)}</span>
          <span>{m.pendente ? "—" : m.real}</span>
          <span className="forte">{m.pendente ? "aguardando" : percentualComSinal(m.erro)}</span>
        </React.Fragment>
      ))}
    </Grade>
  );
}

function Fotos({ fotos, compacto }) {
  if (fotos.length === 0) return <Vazio>Nenhuma foto da planilha guardada ainda.</Vazio>;
  if (compacto) {
    return (
      <CardList>
        {fotos.map((foto) => (
          <CardItem key={foto.id}>
            <CardTopo>
              <CardTitulo>{dataCompleta(foto.data_referencia)}</CardTitulo>
              <span className="selo">{ROTULO_ORIGEM[foto.origem] || foto.origem}</span>
            </CardTopo>
            <CardSub title={foto.nome_arquivo}>{foto.nome_arquivo || "—"}</CardSub>
            <CardMeta>
              <span className="par">
                <b>{foto.total_linhas}</b> <span className="k">linhas</span>
              </span>
              <span className="par">
                <span className="sep" aria-hidden="true">· </span>
                <span className="k">versão</span> {String(foto.versao_arquivo || "").slice(0, 8)}
              </span>
            </CardMeta>
          </CardItem>
        ))}
      </CardList>
    );
  }
  return (
    <Grade $colunas={COLUNAS_FOTOS} role="table" aria-label="Fotos guardadas da planilha">
      <span className="cab">Data</span>
      <span className="cab">Origem</span>
      <span className="cab">Arquivo</span>
      <span className="cab">Versão</span>
      {fotos.map((foto) => (
        <React.Fragment key={foto.id}>
          <span className="forte">{dataCompleta(foto.data_referencia)}</span>
          <span>{ROTULO_ORIGEM[foto.origem] || foto.origem}</span>
          <span title={foto.nome_arquivo}>
            {foto.nome_arquivo || "—"} · {plural(foto.total_linhas, "linha", "linhas")}
          </span>
          <span title={foto.versao_arquivo}>{String(foto.versao_arquivo || "").slice(0, 8)}</span>
        </React.Fragment>
      ))}
    </Grade>
  );
}

function ImportarCopia({ hoje, onImportar }) {
  const toast = useToast();
  const [arquivo, setArquivo] = useState(null);
  const [dataReferencia, setDataReferencia] = useState("");
  const [chaveCampo, setChaveCampo] = useState(0);

  const importar = async () => {
    const resposta = await onImportar({ arquivo, dataReferencia });
    toast.success(
      resposta?.criada
        ? `Cópia de ${dataCompleta(dataReferencia)} guardada no histórico.`
        : "Esta cópia já estava guardada no histórico.",
    );
    setArquivo(null);
    setDataReferencia("");
    // Remonta o <input type=file>: é o único jeito de limpar o arquivo escolhido.
    setChaveCampo((n) => n + 1);
  };

  return (
    <Bloco>
      <h2>Importar cópia antiga</h2>
      <Regua>
        Use as cópias que o escritório guardou da planilha (de meses ou anos anteriores) para completar o histórico do
        ritmo. Informe a data em que cada cópia foi tirada. A mesma cópia importada duas vezes não duplica nada.
      </Regua>
      <Campos>
        <label>
          Arquivo da planilha
          <input
            key={chaveCampo}
            type="file"
            accept=".xlsm,.xlsx"
            onChange={(evento) => setArquivo(evento.target.files?.[0] || null)}
          />
        </label>
        <label>
          Data da cópia
          <input type="date" value={dataReferencia} max={hoje} onChange={(evento) => setDataReferencia(evento.target.value)} />
        </label>
        <AsyncButton
          variant="secondary"
          disabled={!arquivo || !dataReferencia || dataReferencia > hoje}
          silentSuccess
          onClick={importar}
        >
          Importar cópia
        </AsyncButton>
      </Campos>
    </Bloco>
  );
}

export default function AbaPrecisao({ precisao, fotos, hoje, compacto, podeImportar, onImportar }) {
  const medida = precisao?.fonte === FONTE_MEDIDA;
  return (
    <>
      <Aviso $tom="neutral">
        <div>
          Faixa de erro usada na página: <strong>{textoFaixaDePrecisao(precisao)}</strong>{" "}
          ({medida ? textoFonteDaPrecisao(precisao) : `referência: ${textoFonteDaPrecisao(precisao)}`}).{" "}
          {medida
            ? "Ela é atualizada sozinha a cada foto que amadurece."
            : "A medida própria da fábrica passa a valer quando houver 3 fotos maduras."}
        </div>
      </Aviso>
      <Bloco data-pdf="capacidade-precisao">
        <h2>Previsto × real</h2>
        <Regua>
          Uma foto só é comparada depois de {MATURACAO_DU} dias úteis do escoamento: o término costuma ser lançado com
          atraso. Erro positivo = a fila levou mais tempo que o previsto.
        </Regua>
        <Medicoes linhas={linhasDeMedicao(precisao)} compacto={compacto} />
      </Bloco>
      <Bloco>
        <h2>
          Fotos guardadas <span>{fotos.length}</span>
        </h2>
        <Fotos fotos={fotos} compacto={compacto} />
      </Bloco>
      {podeImportar ? <ImportarCopia hoje={hoje} onImportar={onImportar} /> : null}
    </>
  );
}
